import { FormController } from "$root/third-party-integrations/react-hook-form";
import { valueByPath } from "$root/utils";
import type { StylableProps } from "@mdotm/shadcn/legacy/components";
import { type CheckboxProps, Checkbox, FormField } from "@mdotm/shadcn/mdotm/components";
import type { ReactNode } from "react";
import type { Control, ControllerProps, FieldValues, FormState, Path } from "react-hook-form";

export type CheckboxFieldProps<T extends FieldValues> = StylableProps & {
	control: Control<T, any>;
	formState: FormState<T>;
	name: Path<T>;
	rules?: ControllerProps<T>["rules"];
	label?: ReactNode;
	children?: ReactNode;
} & Omit<CheckboxProps, "checked">;

export function CheckboxField<T extends FieldValues>({
	label,
	name,
	rules,
	formState,
	control,
	className,
	style,
	children,
	...forward
}: CheckboxFieldProps<T>): React.JSX.Element {
	return (
		<FormField
			className={className}
			style={style}
			label={label}
			error={(valueByPath(formState.errors, name) as { message?: string })?.message}
		>
			{(fieldProps) => (
				<FormController
					control={control}
					name={name}
					rules={rules}
					// `id` and `invalid` are named rather than spread as `{...fieldProps}`, which is what the
					// other field wrappers do — those are the only two keys `FormField` renders, and naming
					// them keeps an untyped render-prop bag out of a typed component. Both sit BEFORE
					// `{...forward}`, so an explicitly-passed `invalid` still wins.
					//
					// `invalid` was absent until MDOT-24854, because the legacy `Checkbox` this used to wrap
					// had no error state to forward it to. Nothing paints it today either: no boolean field in
					// `frontend/src` carries a rule that can fail — no `.refine()` on a `z.boolean()`, no
					// `z.literal(true)`, no `superRefine` adding an issue at a boolean's path — and the app
					// calls react-hook-form's `setError` nowhere. The case this is wired for is a bare
					// required `z.boolean()` left unseeded, which errors "Required" at its own path.
					render={({ field: { ref, value, onChange, ...controllerProps } }) => (
						<Checkbox
							innerRef={ref}
							id={fieldProps.id}
							invalid={fieldProps.invalid}
							{...controllerProps}
							{...forward}
							onChange={(x) => {
								onChange(x);
								forward.onChange?.(x);
							}}
							checked={value}
						>
							{children}
						</Checkbox>
					)}
				/>
			)}
		</FormField>
	);
}
